import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ChatCircleDots, X, PaperPlaneTilt, ArrowClockwise, Sparkle } from "@phosphor-icons/react";
import { askBhtAI } from "@/lib/api";

export default function AskBHTAI() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const sessionRef = useRef(null);
  const abortRef = useRef(null);
  const scrollRef = useRef(null);

  const lang = i18n.language || "en";
  const suggested = t("ai_widget.suggested", { returnObjects: true }) || [];

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const clear = () => {
    if (abortRef.current) abortRef.current.abort();
    sessionRef.current = null;
    setMessages([]);
    setStreaming(false);
    setInput("");
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "", citations: [] }]);
    setStreaming(true);
    const handle = await askBhtAI(
      { message: msg, sessionId: sessionRef.current, lang },
      {
        onSession: (s) => (sessionRef.current = s),
        onCitations: (cites) => {
          setMessages((m) => {
            const copy = m.slice();
            const last = copy[copy.length - 1];
            if (last && last.role === "assistant") {
              copy[copy.length - 1] = { ...last, citations: cites };
            }
            return copy;
          });
        },
        onDelta: (chunk) => {
          setMessages((m) => {
            const copy = m.slice();
            const last = copy[copy.length - 1];
            if (last && last.role === "assistant") {
              copy[copy.length - 1] = { ...last, content: last.content + chunk };
            }
            return copy;
          });
        },
        onError: (err) => {
          setMessages((m) => {
            const copy = m.slice();
            const last = copy[copy.length - 1];
            if (last && last.role === "assistant" && !last.content) {
              copy[copy.length - 1] = { ...last, content: `⚠ ${err}` };
            }
            return copy;
          });
          setStreaming(false);
        },
        onDone: () => setStreaming(false),
      }
    );
    abortRef.current = handle;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    send();
  };

  return (
    <>
      {/* Trigger */}
      <motion.button
        aria-label={t("ai_widget.title")}
        onClick={() => setOpen(true)}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: open ? 0 : 1, scale: open ? 0.9 : 1 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-bht-navy2/80 backdrop-blur-xl transition-transform hover:scale-105 focus:outline-none"
        style={{ boxShadow: "0 0 0 1px rgba(200,160,69,0.55), 0 20px 60px rgba(0,0,0,0.4), 0 0 40px rgba(200,160,69,0.25)" }}
        data-testid="ai-widget-trigger"
      >
        <ChatCircleDots size={22} weight="regular" className="text-bht-gold" />
        <span className="absolute -top-1 -right-1 flex h-3 w-3">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-bht-gold/40" />
          <span className="relative inline-flex h-3 w-3 rounded-full bg-bht-gold" />
        </span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-6 right-6 z-40 flex h-[560px] w-[380px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl glass-strong sm:w-[420px]"
            style={{ boxShadow: "0 30px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(200,160,69,0.2)" }}
            data-testid="ai-widget-panel"
            role="dialog"
            aria-label={t("ai_widget.title")}
          >
            <div className="flex items-center justify-between border-b border-white/[0.06] px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="relative flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-bht-navy to-bht-void" style={{ boxShadow: "inset 0 0 0 1px rgba(200,160,69,0.4)" }}>
                  <Sparkle size={16} weight="fill" className="text-bht-gold" />
                </div>
                <div className="leading-tight">
                  <div className="font-serif text-lg">{t("ai_widget.title")}</div>
                  <div className="text-[11px] text-white/50">{t("ai_widget.sub")}</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={clear}
                  className="rounded-md p-2 text-white/60 hover:bg-white/5 hover:text-bht-gold"
                  aria-label={t("ai_widget.clear")}
                  data-testid="ai-widget-clear"
                >
                  <ArrowClockwise size={14} />
                </button>
                <button
                  onClick={() => setOpen(false)}
                  className="rounded-md p-2 text-white/60 hover:bg-white/5 hover:text-white"
                  aria-label="Close"
                  data-testid="ai-widget-close"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5" data-testid="ai-widget-messages">
              {messages.length === 0 ? (
                <div className="flex h-full flex-col justify-between">
                  <p className="text-sm leading-relaxed text-white/60">{t("ai_widget.empty")}</p>
                  <div className="mt-6 flex flex-col gap-2">
                    <div className="eyebrow mb-1">Try</div>
                    {(suggested || []).map((s, i) => (
                      <button
                        key={i}
                        onClick={() => send(s)}
                        className="group flex items-center justify-between rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2.5 text-left text-[13px] text-white/80 transition-colors hover:border-bht-gold/40 hover:text-bht-gold"
                        data-testid={`ai-widget-suggested-${i}`}
                      >
                        <span>{s}</span>
                        <span className="text-white/30 transition-colors group-hover:text-bht-gold">→</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((m, i) => (
                    <div key={i} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                      <div
                        className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-[13.5px] leading-relaxed ${
                          m.role === "user"
                            ? "bg-bht-gold text-bht-void"
                            : "bg-white/[0.04] text-white/90"
                        }`}
                      >
                        {m.content || <span className="inline-block h-2 w-2 rounded-full bg-bht-gold blink" />}
                        {streaming && i === messages.length - 1 && m.role === "assistant" && m.content && (
                          <span className="ml-1 inline-block h-3 w-[2px] translate-y-0.5 bg-bht-gold blink" />
                        )}
                      </div>
                      {m.role === "assistant" && m.citations && m.citations.length > 0 && (
                        <div className="mt-2 flex max-w-[85%] flex-wrap gap-1.5" data-testid={`ai-widget-citations-${i}`}>
                          {m.citations.slice(0, 3).map((c, j) => (
                            <a
                              key={j}
                              href={c.url}
                              className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-white/60 transition-colors hover:border-bht-gold/50 hover:text-bht-gold"
                            >
                              <span className="h-1 w-1 rounded-full bg-bht-gold" />
                              {c.title.length > 40 ? c.title.slice(0, 40) + "…" : c.title}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <form onSubmit={onSubmit} className="border-t border-white/[0.06] px-3 py-3" data-testid="ai-widget-form">
              <div className="flex items-end gap-2 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2 focus-within:border-bht-gold/50">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  rows={1}
                  placeholder={t("ai_widget.placeholder")}
                  className="max-h-32 flex-1 resize-none bg-transparent py-2 text-sm text-white placeholder:text-white/40 focus:outline-none"
                  aria-label={t("ai_widget.placeholder")}
                  data-testid="ai-widget-input"
                />
                <button
                  type="submit"
                  disabled={streaming || !input.trim()}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-bht-gold text-bht-void transition-transform hover:scale-105 disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label={t("ai_widget.send")}
                  data-testid="ai-widget-send"
                >
                  <PaperPlaneTilt size={14} weight="fill" />
                </button>
              </div>
              <div className="mt-2 flex items-center justify-between px-1 text-[10px] uppercase tracking-widest text-white/30">
                <span>Claude Sonnet 4.5 · RAG</span>
                <span>{streaming ? `${t("ai_widget.thinking")}…` : ""}</span>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
